/**
 * Push Notification Service
 * Handles Service Worker registration and push subscription management.
 *
 * Nutzt den zentralen Axios-Client (mit Auth-Interceptor), damit der Token
 * automatisch aus localStorage gelesen wird — kein manuelles Token-Handling.
 */

import api from './api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window;
}

// Get current permission status
export function getPermissionStatus() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker nicht unterstützt');
  }
  const registration = await navigator.serviceWorker.register('/sw.js');
  return registration;
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Benachrichtigungen werden nicht unterstützt');
  }
  return Notification.requestPermission();
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push-Benachrichtigungen werden nicht unterstützt');
  }

  const registration = await navigator.serviceWorker.ready;

  // VAPID-Key vom Backend
  const { data } = await api.get('/push/vapid-key');
  const publicKey = data.public_key;
  if (!publicKey) throw new Error('VAPID-Schlüssel fehlt');

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  await api.post('/push/subscribe', subscription.toJSON());
  return subscription;
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await subscription.unsubscribe();
    try {
      await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
    } catch {
      /* Falls Backend bereits abgelaufen — nicht blockieren */
    }
  }
  return true;
}

// Check if user is currently subscribed
export async function isSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

// Initialize push notifications (call on app start)
export async function initPushNotifications() {
  if (!isPushSupported()) return { supported: false };
  try {
    await registerServiceWorker();
    const alreadySubscribed = await isSubscribed();
    const permission = getPermissionStatus();
    return { supported: true, permission, subscribed: alreadySubscribed };
  } catch (error) {
    return { supported: true, permission: 'default', subscribed: false, error: error.message };
  }
}

// Expose for legacy code that imports API_URL
export { API_URL };
